import {
  ArrowDropDown,
  EuroOutlined,
  ManageAccountsOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import omit from 'lodash-es/omit';
import { useMemo, useState, type MouseEventHandler, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { LanguageType, StyleObj } from '../../../@types';
import { ACCOUNT_TABS, AccountMenuItem, AccountMenuTab } from '../../../config/accountMenu';
import { ODD_CHANGES } from '../../../constants';
import { ODDS_FORMAT_OPTIONS } from '../../../constants/odds';
import useBreakpoints from '../../../hooks/useBreakpoints';
import useLocalization from '../../../hooks/useLocalization';
import useProductAvailability from '../../../hooks/useProductAvailability';
import useTabs from '../../../hooks/useTabs';
import { useAvailableLanguages } from '../../../hooks/useAvailableLanguages';
import { TabItem } from '../../../hooks/useHeaderTabs';
import { useLanguage } from '../../../localization/LanguageInstanceProvider';
import { useUpdateUserData, useUserData } from '../../../queries';
import { buildUserSettingsUpdate } from '../../../utils/userData';
import DepositButton from '../../atoms/DepositButton';
import { FormattedBalance } from '../../atoms/FormattedBalance';
import HistoryIcon from '../../icons/HistoryIcon';
import MessagesIcon from '../../icons/MessagesIcon';
import ReloadIcon from '../../icons/ReloadIcon';
import { AccountMenuButton } from './AccountMenuButton';
import { useAccountMenu } from '../../../hooks/useAccountMenu';

import { mergeSx } from '../../../utils/styles';

const styles: StyleObj = {
  username: {
    display: {
      xs: 'none',
      md: 'block',
    },
  },
  balance: {
    fontSize: { xs: 9, md: 12 },
    textAlign: 'right',
  },
  menuContainer: {
    mt: 2,
    '& .MuiPaper-root': {
      width: 360,
      backgroundColor: 'neutral.25',
      color: 'neutral.800',
    },
    '& .MuiList-root': {
      py: 0,
    },
  },
  tab: {
    px: 0.5,
    textTransform: 'none',
    color: 'neutral.500',
    '& svg': {
      flex: 1,
    },
    '&.Mui-selected': {
      backgroundColor: 'neutral.50',
      '& p': {
        fontWeight: 600,
      },
    },
  },
  accordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': { display: 'none' },
  },
  accordionDetails: { flexDirection: 'column', p: 0 },
  menuItem: {
    backgroundColor: 'neutral.50',
    borderBottom: '1px solid',
    borderColor: 'neutral.100',
    minHeight: 40,
    '&:hover': { backgroundColor: 'neutral.200' },
  },
  childMenuItem: {
    backgroundColor: 'neutral.100',
    '&:hover': { backgroundColor: 'neutral.200' },
    flex: 1,
    '&.MuiMenuItem-root': {
      minHeight: 40,
      '&:not(:last-child)': {
        borderBottom: '1px solid',
        borderColor: 'neutral.200',
      },
    },
  },
  logoutButton: {
    py: 1.5,
    backgroundColor: 'neutral.25',
    ':hover': {
      border: 'none',
    },
  },
  accountLabel: {
    flex: 1,
    p: 0.25,
    '& svg': {
      height: '32px',
      width: '32px',
    },
    cursor: 'pointer',
    '&:hover': {
      color: 'primary.main',
    },
  },
};

type PlayerAccountMenuTriggerProps = {
  isOpen: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  showUnreadBadge: boolean;
};

type PlayerAccountMenuProps = {
  showUnreadBadge?: boolean;
  showSummary?: boolean;
  renderTrigger?: (props: PlayerAccountMenuTriggerProps) => ReactNode;
};

const PlayerAccountMenu = ({ showUnreadBadge = true, showSummary = true, renderTrigger }: PlayerAccountMenuProps) => {
  const [showBalance, setShowBalance] = useState(true);
  const { isMobile } = useBreakpoints();
  const { setSelectedLanguage } = useLanguage();
  const navigate = useNavigate();
  const { hasSportsbookAccess, isCasinoOnlyProduct } = useProductAvailability();

  const { anchorEl, isMenuOpen, preferredUsername, handleAccountMenuOpen, handleAccountMenuClose, handleLogoutClick } =
    useAccountMenu();

  const availableLanguages = useAvailableLanguages();
  const { t, getLocalizedConfig } = useLocalization();

  const localizedAccountTabs = useMemo(() => getLocalizedConfig(ACCOUNT_TABS), [getLocalizedConfig]);
  const localizedOddChanges = useMemo(() => getLocalizedConfig(ODD_CHANGES), [getLocalizedConfig]);
  const localizedOddsFormat = useMemo(() => getLocalizedConfig(ODDS_FORMAT_OPTIONS), [getLocalizedConfig]);

  const tabProps = useTabs<AccountMenuTab>({ tabConfig: localizedAccountTabs });

  const ACCOUNT_MENU_ITEM_CONFIG: Record<AccountMenuTab, AccountMenuItem[]> = useMemo(
    () => ({
      'my-account': [
        {
          label: t('myAccount'),
          value: 'my-account',
          icon: <ManageAccountsOutlined />,
          to: '/my-account',
        },
        { label: t('bonus'), value: 'bonus', icon: <EuroOutlined />, to: '/my-account/bonus' },
        { label: t('history'), value: 'history', icon: <HistoryIcon />, to: '/my-account/history' },
        { label: t('messages'), value: 'messages', icon: <MessagesIcon />, to: '/my-account/messages' },
      ],
      preferences: [
        {
          sectionLabel: t('selectLanguage'),
          value: 'language',
          children: availableLanguages,
        },
        ...(hasSportsbookAccess
          ? [
              {
                sectionLabel: t('acceptOddChanges'),
                value: 'oddsChanges',
                children: localizedOddChanges,
              },
              {
                sectionLabel: t('oddsFormat'),
                value: 'oddsFormat',
                children: localizedOddsFormat,
              },
            ]
          : []),
      ],
    }),
    [availableLanguages, hasSportsbookAccess, localizedOddChanges, localizedOddsFormat, t]
  );

  const { data: userSettings } = useUserData();
  const flattenUserSettings = { ...userSettings, ...userSettings?.sportsbookConfig };
  const { mutate: updateUserSettings } = useUpdateUserData();

  const handleMenuItemClick = ({ to, value, key }: { to?: string; value?: string; key?: string }) => {
    if (!to && !key && !value) {
      return;
    }

    handleAccountMenuClose();

    if (to) {
      return navigate(to);
    }

    if (key && value) {
      const updatedSettings = buildUserSettingsUpdate(key, value);
      updateUserSettings(updatedSettings);

      if (value === 'language') {
        setSelectedLanguage(key as LanguageType);
      }
    }
  };

  if (!userSettings) {
    return null;
  }

  const trigger = renderTrigger ? (
    renderTrigger({ onClick: handleAccountMenuOpen, showUnreadBadge, isOpen: isMenuOpen })
  ) : (
    <AccountMenuButton onClick={handleAccountMenuOpen} showUnreadBadge={showUnreadBadge} />
  );

  const showBonusCredits = !isCasinoOnlyProduct;

  return (
    <>
      <Stack direction={showSummary && isMobile ? 'column-reverse' : 'row'} spacing={showSummary ? 0.5 : 0}>
        {showSummary && showBalance && (
          <Box>
            <Typography variant='h6' sx={styles.username}>
              {preferredUsername}
            </Typography>
            <Typography variant='body3' sx={styles.balance}>
              <FormattedBalance balanceType='main' />
            </Typography>
          </Box>
        )}
        {trigger}
      </Stack>
      {isMenuOpen && (
        <Menu
          anchorEl={anchorEl}
          open
          onClose={handleAccountMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          elevation={2}
          sx={styles.menuContainer}
        >
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
            }}
          >
            <Stack
              spacing={1.5}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              {showBalance && (
                <Stack spacing={0.25}>
                  <Typography variant='body3'>{preferredUsername}</Typography>
                  <Typography
                    variant='body3'
                    color='primary'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <FormattedBalance balanceType='main' />
                  </Typography>
                </Stack>
              )}
              {showBalance && showBonusCredits && (
                <Stack spacing={0.25}>
                  <Typography variant='body3'>{t('bonusCredits')}:</Typography>
                  <Typography
                    variant='body3'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <FormattedBalance balanceType='bonus' />
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Stack
              spacing={1}
              sx={{
                alignItems: 'flex-end',
                justifyContent: 'space-between',
              }}
            >
              <DepositButton onClick={handleAccountMenuClose} />
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                }}
              >
                <IconButton onClick={() => setShowBalance((prev) => !prev)} sx={styles.iconButton}>
                  {showBalance ? <VisibilityOutlined fontSize='small' /> : <VisibilityOffOutlined fontSize='small' />}
                </IconButton>
                <IconButton sx={styles.iconButton}>
                  <ReloadIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>

          <Divider />
          <Tabs variant='fullWidth' {...omit(tabProps, 'setValue')} sx={styles.tabs}>
            {localizedAccountTabs.map((tab: TabItem) => (
              <Tab
                disableRipple
                key={tab.value}
                label={<Typography variant='body3'>{tab.label}</Typography>}
                value={tab.value}
                sx={styles.tab}
              />
            ))}
          </Tabs>
          <Divider />
          <MenuList>
            {tabProps.value === 'my-account' ? (
              <Stack
                direction='row'
                sx={{
                  mx: 1,
                  my: 3,
                }}
              >
                {ACCOUNT_MENU_ITEM_CONFIG[tabProps.value].map(({ label, to, icon }) => (
                  <Stack
                    onClick={() => handleMenuItemClick({ to })}
                    key={label}
                    direction='column'
                    sx={mergeSx(
                      {
                        alignItems: 'center',
                        gap: 1,
                      },
                      styles.accountLabel
                    )}
                  >
                    {!!icon && icon}
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        flexWrap: 'wrap',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              ACCOUNT_MENU_ITEM_CONFIG[tabProps.value].map(({ value, to, sectionLabel, children }) => (
                <Accordion disableGutters key={value} onClick={() => handleMenuItemClick({ to })} sx={styles.accordion}>
                  <AccordionSummary
                    expandIcon={children?.length && <ArrowDropDown fontSize='small' />}
                    sx={styles.menuItem}
                  >
                    <Stack direction='column'>
                      {sectionLabel && <Typography variant='body3'>{sectionLabel}</Typography>}
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          flex: 1,
                        }}
                      >
                        {
                          children?.find(
                            (item) => item.key === flattenUserSettings[value as keyof typeof flattenUserSettings]
                          )?.label
                        }
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={styles.accordionDetails}>
                    {children?.map(({ label, key }) => (
                      <MenuItem
                        key={label}
                        onClick={() => handleMenuItemClick({ value, key })}
                        disableRipple
                        sx={styles.childMenuItem}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            flex: 1,
                          }}
                        >
                          {label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
            <MenuItem disableRipple sx={styles.logoutButton} onClick={handleLogoutClick}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logout')}
              </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </>
  );
};

export default PlayerAccountMenu;
